import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, HandHelping } from "lucide-react";

/**
 * Add-a-Helper-Designer modal (Design Manager on their own project).
 * Fetches ALL active Interior Designers from any branch, not just the DM's team.
 * A reason is required (visible only to Admin/Director/DH).
 * Cannot change the primary Design Manager here — use reassign-full for that.
 */
export default function AddHelperDesignerModal({ open, project, onClose, onSaved }) {
  const [designers, setDesigners] = useState([]);
  const [helperId, setHelperId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    setErr(""); setReason(""); setHelperId("");
    // Fetch every active designer from any branch. Backend endpoint accepts no manager_id → all.
    (async () => {
      try {
        const { data } = await api.get("/design/designers");
        // Filter out the primary designer + already-added helpers.
        const already = new Set([
          project?.interior_designer_id,
          ...(project?.helper_designer_ids || []),
        ]);
        setDesigners(data.filter((d) => !already.has(d.id)));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, [open, project]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!helperId) { setErr("Please select a designer"); return; }
    if (reason.trim().length < 6) { setErr("Reason must be at least 6 characters"); return; }
    setSaving(true);
    try {
      await api.post(`/design/projects/${project.id}/add-helper`, {
        helper_designer_id: helperId, reason: reason.trim(),
      });
      toast.success("Helper designer added");
      onSaved();
    } catch (er) {
      setErr(formatApiErrorDetail(er.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-testid="add-helper-modal">
      <div className="w-full max-w-md iz-card p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded hover:bg-[#1c1c1c] text-gray-500"><X className="w-4 h-4"/></button>
        <div className="flex items-center gap-3 mb-4">
          <HandHelping className="w-5 h-5 text-[#d4af37]"/>
          <div>
            <p className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#d4af37]">Add Helper</p>
            <h3 className="font-display text-xl text-white">{project?.project_name}</h3>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Helper Designer (any branch) *</label>
            <select value={helperId} onChange={(e) => setHelperId(e.target.value)} className="iz-input" required data-testid="helper-designer-select">
              <option value="">— Select an Interior Designer —</option>
              {designers.map((d) => (
                <option key={d.id} value={d.id}>{d.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-mono uppercase tracking-widest text-gray-400 mb-2">Reason *</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="iz-input"
              rows={3}
              minLength={6}
              maxLength={500}
              required
              placeholder="Why is this helper needed?"
              data-testid="helper-reason-input"
            />
          </div>
          {err && <p className="text-xs text-red-400 border border-red-500/40 bg-red-500/5 rounded px-3 py-2">{err}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="iz-btn-secondary text-xs" onClick={onClose}>Cancel</button>
            <button type="submit" disabled={saving} className="iz-btn-primary text-xs disabled:opacity-50" data-testid="add-helper-submit-btn">
              {saving ? "Adding…" : "Add Helper"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
